import React, { createContext, useContext, ReactNode } from "react";

import { EventBus } from "../events/EventBus";
import { ServiceRegistry } from "../services/ServiceRegistry";

import { GateManager } from "./GateManager";
import { AppState, StateEvent } from "./types";
import { useStateMachine } from "./useStateMachine";

interface StateMachineContextValue {
  // State
  currentState: AppState;
  currentPhase: string | null;
  dataRequirements: unknown | null;
  allowedRoutes: unknown | null;

  // Actions
  transition: (event: StateEvent) => boolean;
  canTransition: (event: StateEvent) => boolean;
  getAvailableEvents: () => StateEvent[];
  reset: () => void;

  // Navigation
  isRouteAllowed: (path: string) => boolean;
  getRedirectPath: () => string | null;

  // Utilities
  isInPhase: (state: AppState) => boolean;
  getPhaseDescription: () => string;

  // Gate Control
  closeGate: (gateName: string) => void;
  openGate: (gateName: string) => void;
  isGateClosed: (gateName: string) => boolean;
  getGateStatuses: () => Record<string, unknown>;

  // Service Registry
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getService: <T = any>(name: string) => T | undefined;
  getServiceRegistry: () => ServiceRegistry | undefined;
}

const StateMachineContext = createContext<StateMachineContextValue | null>(
  null,
);

interface StateMachineProviderProps {
  children: ReactNode;
  eventBus?: EventBus;
  serviceRegistry?: ServiceRegistry;
  gateManager?: GateManager;
  autoStart?: boolean;
  onStateChange?: (from: AppState, to: AppState, event: StateEvent) => void;
  onError?: (error: Error, context: string) => void;
}

export const StateMachineProvider: React.FC<StateMachineProviderProps> = ({
  children,
  eventBus,
  serviceRegistry,
  gateManager,
  autoStart = true,
  onStateChange,
  onError,
}) => {
  const stateMachine = useStateMachine({
    eventBus,
    serviceRegistry,
    gateManager,
    autoStart,
    onStateChange,
    onError,
  });

  const contextValue: StateMachineContextValue = {
    // State
    currentState: stateMachine.currentState,
    currentPhase: stateMachine.currentPhase?.state || null,
    dataRequirements: stateMachine.dataRequirements,
    allowedRoutes: stateMachine.allowedRoutes,

    // Actions
    transition: stateMachine.transition,
    canTransition: stateMachine.canTransition,
    getAvailableEvents: stateMachine.getAvailableEvents,
    reset: stateMachine.reset,

    // Navigation
    isRouteAllowed: stateMachine.isRouteAllowed,
    getRedirectPath: stateMachine.getRedirectPath,

    // Utilities
    isInPhase: stateMachine.isInPhase,
    getPhaseDescription: stateMachine.getPhaseDescription,

    // Gate Control
    closeGate: gateManager?.closeGate.bind(gateManager) || (() => {}),
    openGate: gateManager?.openGate.bind(gateManager) || (() => {}),
    isGateClosed: gateManager?.isGateClosed.bind(gateManager) || (() => false),
    getGateStatuses:
      gateManager?.getGateStatuses.bind(gateManager) || (() => ({})),

    // Service Registry
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getService: <T = any,>(name: string): T | undefined => {
      return serviceRegistry?.get<T>(name);
    },
    getServiceRegistry: (): ServiceRegistry | undefined => {
      return serviceRegistry;
    },
  };

  return (
    <StateMachineContext.Provider value={contextValue}>
      {children}
    </StateMachineContext.Provider>
  );
};

export const useStateMachineContext = (): StateMachineContextValue => {
  const context = useContext(StateMachineContext);
  if (!context) {
    throw new Error(
      "useStateMachineContext must be used within a StateMachineProvider",
    );
  }
  return context;
};

export default StateMachineProvider;
